import {
  alpha,
  Box,
  ButtonGroup,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import dayjs, { type Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import CustomDatePicker from 'components/DatePIcker/CustomDatePicker';
import { useEffect, useMemo, useState } from 'react';
import { transformMasterAlgoToPlanDataByBumperNo } from 'views/FleetPlanner/utils/dataFormatters';
import { useAppSelector } from 'store/hook';
import { getPlanTimeRange } from '../utils/dataGetters';
import { type GeoTrip } from 'types/geo.types';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { Cached } from '@mui/icons-material';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { ReSchedule } from './ReSchedule';
import { mergeDispatchPlans } from '../utils/dataFormatters';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import useAppTheme from 'hooks/useAppTheme';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);
dayjs.extend(timezone);
const dateRangeList = [
  { label: 'Today', value: 'Today' },
  { label: 'Yesterday', value: '1D' },
  { label: 'Custom', value: 'Custom' },
];

export const HeaderWithFilters = ({
  tripsData,
  loading,
  selectedDateRange,
  handleDateChange,
  date,
  currentShiftLabel,
  availableShifts,
  selectedShiftNumber,
  onSelectedShiftChange,
}: {
  tripsData: Record<string, GeoTrip[]>;
  loading: boolean;
  selectedDateRange: string;
  date: dayjs.Dayjs;
  handleDateChange: (
    range: string,
    date: Dayjs | null,
    shouldFetchActivePlan: boolean,
    isButtonClick?: boolean,
    options?: {
      shiftViewModeOverride?: 'current' | 'shiftWise';
      shiftNumberOverride?: number;
    }
  ) => Promise<void>;
  currentShiftLabel?: string;
  availableShifts: Array<{ shiftNumber: number; label: string }>;
  selectedShiftNumber?: number;
  onSelectedShiftChange: (shiftNumber: number) => void;
}) => {
  const { t } = useTranslation();
  const [internalRange, setInternalRange] = useState(selectedDateRange);
  const [internalDate, setInternalDate] = useState(date);

  const { customerTz } = useCustomerInfo();
  const { enableDarkTheme } = useAppTheme();

  const { yesterdayInTz } = useMemo(() => {
    return {
      yesterdayInTz: dayjs().tz(customerTz).subtract(1, 'day'),
      todayInTz: dayjs().tz(customerTz),
    };
  }, [customerTz]);

  const formattedMasterAlgoTripsData = useMemo(
    () => transformMasterAlgoToPlanDataByBumperNo(tripsData),
    [tripsData]
  );
  const approvedPlanData = useAppSelector(
    (state) => state.fleetPlanningReducer.dispatchPlanSummary
  );

  const mergedPlans = mergeDispatchPlans(approvedPlanData.plans);

  const { masterRange } = useMemo(
    () =>
      getPlanTimeRange(
        formattedMasterAlgoTripsData.planData,
        mergedPlans.planData,
        customerTz,
        false
      ),
    [customerTz, formattedMasterAlgoTripsData.planData, mergedPlans.planData]
  );

  const handleRangeChange = (
    range: string,
    date: Dayjs | null,
    shouldFetchActivePlan: boolean,
    isButtonClick?: boolean
  ) => {
    setInternalRange(range); // update internal do not update parent
    if (range !== 'Custom') {
      void handleDateChange(range, date, shouldFetchActivePlan, isButtonClick);
    }
  };

  const handleApplyClick = () => {
    void handleDateChange('Custom', internalDate, true, true);
  };

  useEffect(() => {
    setInternalRange(selectedDateRange);
  }, [selectedDateRange]);

  useEffect(() => {
    setInternalDate(date);
  }, [date]);

  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  return (
    <Paper
      sx={{
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'row',
        gap: 2,
        background: 'transparent',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
      elevation={0}
    >
      <Stack direction="row" gap={4} alignItems="center">
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '1.3rem',
              color: 'text.primary',
            }}
          >
            {'Dispatch Summary'}
          </Typography>
          {currentShiftLabel && (
            <Typography
              sx={{
                fontSize: '0.85rem',
                color: 'text.secondary',
              }}
            >
              {`Current shift: ${currentShiftLabel}`}
            </Typography>
          )}
        </Box>
        <Stack direction={'row'} alignItems="center" gap={1}>
          <ButtonGroup
            size="small"
            variant="outlined"
            aria-label={t('basic_button_group')}
            sx={{
              border: '1px solid',
              borderColor: 'borderColor.grey',
              borderRadius: 2,
            }}
            disabled={loading}
          >
            {dateRangeList.map((item) => (
              <CustomLoadingButton
                disabled={loading}
                key={item.value}
                size="small"
                sx={{
                  backgroundColor:
                    internalRange === item.value
                      ? palette.primary
                      : alpha(palette.primary, 0.3),
                  border: 'none',
                }}
                onClick={() => {
                  if (item.value === internalRange) return; // avoid duplicate selection
                  handleRangeChange(item.value, null, true);
                }}
              >
                {item.label}
              </CustomLoadingButton>
            ))}
          </ButtonGroup>

          {internalRange === 'Custom' && (
            <Stack direction={'row'} alignItems="center" gap={1}>
              {customerTz ? (
                <CustomDatePicker
                  value={internalDate}
                  onChange={(value) => {
                    if (value) {
                      setInternalDate(value);
                    }
                  }}
                  maxDate={yesterdayInTz}
                  timezone={customerTz}
                  sx={{
                    width: '14px',
                    minWidth: '150px',
                  }}
                />
              ) : null}

              <Box>
                <CustomLoadingButton
                  sx={{
                    height: '32px',
                    width: '80px',
                  }}
                  onClick={handleApplyClick}
                  loading={loading}
                  disabled={loading}
                >
                  {t('apply')}
                </CustomLoadingButton>
              </Box>
            </Stack>
          )}
        </Stack>

        {internalRange !== 'Today' && availableShifts.length > 1 && (
          <Stack direction="row" alignItems="center" gap={1}>
            <ButtonGroup
              size="small"
              variant="outlined"
              aria-label={t('shift_toggle')}
              sx={{
                border: '1px solid',
                borderColor: 'borderColor.grey',
                borderRadius: 2,
              }}
              disabled={loading}
            >
              {availableShifts.map((shift) => (
                <CustomLoadingButton
                  key={shift.shiftNumber}
                  disabled={loading}
                  size="small"
                  sx={{
                    backgroundColor:
                      selectedShiftNumber === shift.shiftNumber
                        ? palette.primary
                        : alpha(palette.primary, 0.3),
                    border: 'none',
                  }}
                  onClick={() => {
                    if (selectedShiftNumber === shift.shiftNumber) return;
                    onSelectedShiftChange(shift.shiftNumber);
                    void handleDateChange(
                      internalRange,
                      internalRange === 'Custom' ? internalDate : null,
                      true,
                      internalRange === 'Custom',
                      { shiftNumberOverride: shift.shiftNumber }
                    );
                  }}
                >
                  {shift.label}
                </CustomLoadingButton>
              ))}
            </ButtonGroup>
          </Stack>
        )}
      </Stack>

      {internalRange === 'Today' && (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Typography>
            {`Last Updated: ${convertUtcToDisplayTime(
              masterRange.endDateWithTime,
              'configured',
              customerTz
            )}`}
          </Typography>
          <CustomLoadingButton
            disabled={loading}
            startIcon={<Cached />}
            onClick={() => {
              handleRangeChange('Today', null, true);
            }}
            color="secondary"
            sx={{
              height: '32px',
            }}
          >
            {t('boundary_importer_btn_refresh')}
          </CustomLoadingButton>

          {internalRange === 'Today' && !loading ? <ReSchedule /> : null}
        </Stack>
      )}
    </Paper>
  );
};
